import { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { History, RotateCcw, Eye, Trash2 } from "lucide-react";
import WikiSidebar from "./WikiSidebar";
import WikiHeader from "./WikiHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Diff component to show changes between versions
function DiffView({ oldText, newText }) {
  const computeDiff = (oldStr, newStr) => {
    const oldLines = (oldStr || '').split('\n');
    const newLines = (newStr || '').split('\n');
    const result = [];
    
    let i = 0, j = 0;
    while (i < oldLines.length || j < newLines.length) {
      if (i >= oldLines.length) {
        result.push({ type: 'added', text: newLines[j] });
        j++;
      } else if (j >= newLines.length) {
        result.push({ type: 'removed', text: oldLines[i] });
        i++;
      } else if (oldLines[i] === newLines[j]) {
        result.push({ type: 'unchanged', text: oldLines[i] });
        i++; j++;
      } else {
        // Simple heuristic: check if next old line matches current new
        let foundInNew = newLines.slice(j).indexOf(oldLines[i]);
        let foundInOld = oldLines.slice(i).indexOf(newLines[j]);
        
        if (foundInNew !== -1 && (foundInOld === -1 || foundInNew <= foundInOld)) {
          result.push({ type: 'added', text: newLines[j] });
          j++;
        } else {
          result.push({ type: 'removed', text: oldLines[i] });
          i++;
        }
      }
    }
    return result;
  };

  const diff = computeDiff(oldText, newText);

  return (
    <div className="font-mono text-sm border border-[#a2a9b1] rounded bg-white overflow-auto max-h-[60vh]">
      {diff.map((line, idx) => (
        <div 
          key={idx}
          className={`px-3 py-0.5 ${
            line.type === 'added' ? 'bg-green-100 text-green-800' :
            line.type === 'removed' ? 'bg-red-100 text-red-800' :
            ''
          }`}
        >
          <span className="inline-block w-6 text-gray-400">
            {line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' '}
          </span>
          {line.text || ' '}
        </div>
      ))}
    </div>
  );
}

export default function WikiPage() {
  const { title } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(null);
  const [notes, setNotes] = useState(null);
  const [username, setUsername] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [moveReason, setMoveReason] = useState("");
  const [createRedirect, setCreateRedirect] = useState(true);
  
  // History state
  const [history, setHistory] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [viewingVersion, setViewingVersion] = useState(null);
  const [showDiff, setShowDiff] = useState(false);
  
  const activeTab = searchParams.get('tab') || 'article';

  useEffect(() => {
    checkAuth();
  }, [title]);

  // Reset notes and history when title changes to ensure we fetch the correct data for each page
  useEffect(() => {
    setNotes(null);
    setHistory([]);
  }, [title]);

  useEffect(() => {
    if (activeTab === 'notes' && isAuthenticated && !notes) {
      fetchNotes();
    }
    if (activeTab === 'history' && isAuthenticated && history.length === 0) {
      fetchHistory();
    }
  }, [activeTab, isAuthenticated, notes, title]);

  // Handle collapse box toggle clicks
  useEffect(() => {
    const handleCollapseClick = (e) => {
      const header = e.target.closest('.wiki-collapse-header');
      if (!header) return;
      
      const targetId = header.dataset.collapseTarget;
      if (!targetId) return;
      
      const content = document.getElementById(`collapse-content-${targetId}`);
      if (!content) return;
      
      const isHidden = content.style.display === 'none';
      content.style.display = isHidden ? 'block' : 'none';
      
      const toggle = header.querySelector('.wiki-collapse-toggle');
      const hint = header.querySelector('.wiki-collapse-hint');
      
      if (toggle) toggle.textContent = isHidden ? '▼' : '▶';
      if (hint) hint.textContent = isHidden ? '[hide]' : '[show]';
    };
    
    document.addEventListener('click', handleCollapseClick);
    return () => document.removeEventListener('click', handleCollapseClick);
  }, []);

  // Handle citation link clicks - scroll and highlight
  useEffect(() => {
    const handleCitationClick = (e) => {
      const link = e.target.closest('a[href^="#ref-"]');
      if (!link) return;
      
      e.preventDefault();
      const refId = link.getAttribute('href').substring(1); // Remove the #
      const targetElement = document.getElementById(refId);
      
      if (targetElement) {
        // Remove any existing highlights
        document.querySelectorAll('.citation-highlight').forEach(el => {
          el.classList.remove('citation-highlight');
        });
        
        // Scroll to the citation
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Add highlight class after a small delay to let scroll start
        setTimeout(() => {
          targetElement.classList.add('citation-highlight');
          
          // Remove highlight class after animation completes (3s)
          setTimeout(() => {
            targetElement.classList.remove('citation-highlight');
          }, 3000);
        }, 100);
      }
    };
    
    document.addEventListener('click', handleCitationClick);
    return () => document.removeEventListener('click', handleCitationClick);
  }, []);

  // Handle section edit link clicks - use React Router navigation
  useEffect(() => {
    const handleSectionEditClick = (e) => {
      // Allow ctrl+click / cmd+click / middle-click to open in new tab
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
      const link = e.target.closest('a.mw-editsection-link');
      if (!link) return;
      const href = link.getAttribute('href');
      if (href && href.startsWith('/wiki/')) {
        e.preventDefault();
        navigate(href);
      }
    };
    document.addEventListener('click', handleSectionEditClick);
    return () => document.removeEventListener('click', handleSectionEditClick);
  }, [navigate]);

  // Initialize sortable tables
  useEffect(() => {
    const initSortableTables = () => {
      const sortableTables = document.querySelectorAll('.wiki-content table.sortable');
      
      sortableTables.forEach(table => {
        // Skip if already initialized
        if (table.dataset.sortableInit) return;
        table.dataset.sortableInit = 'true';
        
        const headers = table.querySelectorAll('th.sortable-header');
        const tbody = table.querySelector('tbody') || table;
        
        headers.forEach((header, colIndex) => {
          header.addEventListener('click', () => {
            const rows = Array.from(tbody.querySelectorAll('tr')).filter(row => 
              row.querySelector('td') // Only data rows, not header rows
            );
            
            // Determine sort direction
            const isAsc = header.classList.contains('sort-asc');
            const isDesc = header.classList.contains('sort-desc');
            
            // Remove sort classes from all headers
            headers.forEach(h => {
              h.classList.remove('sort-asc', 'sort-desc');
            });
            
            // Set new sort direction
            let sortDir = 'asc';
            if (isAsc) {
              sortDir = 'desc';
              header.classList.add('sort-desc');
            } else {
              header.classList.add('sort-asc');
            }
            
            // Sort rows
            rows.sort((a, b) => {
              const aCell = a.querySelectorAll('td')[colIndex];
              const bCell = b.querySelectorAll('td')[colIndex];
              
              if (!aCell || !bCell) return 0;
              
              let aVal = aCell.textContent.trim();
              let bVal = bCell.textContent.trim();
              
              // Try to parse as numbers
              const aNum = parseFloat(aVal.replace(/[,$]/g, ''));
              const bNum = parseFloat(bVal.replace(/[,$]/g, ''));
              
              // Try to parse as dates
              const aDate = Date.parse(aVal);
              const bDate = Date.parse(bVal);
              
              let comparison = 0;
              if (!isNaN(aNum) && !isNaN(bNum)) {
                comparison = aNum - bNum;
              } else if (!isNaN(aDate) && !isNaN(bDate)) {
                comparison = aDate - bDate;
              } else {
                comparison = aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
              }
              
              return sortDir === 'asc' ? comparison : -comparison;
            });
            
            // Re-append rows in sorted order
            rows.forEach(row => tbody.appendChild(row));
          });
        });
      });
    };
    
    // Initialize after content is rendered
    const timer = setTimeout(initSortableTables, 100);
    return () => clearTimeout(timer);
  }, [page?.html_content, notes?.parsed_html]);

  const checkAuth = async () => {
    const token = localStorage.getItem("wiki_token");
    
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/wiki/verify?token=${token}`);
      if (response.data.valid) {
        setIsAuthenticated(true);
        setUsername(response.data.username);
        fetchPage(token);
      } else {
        localStorage.removeItem("wiki_token");
        localStorage.removeItem("wiki_username");
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const fetchPage = async (token) => {
    try {
      const response = await axios.get(`${API}/wiki/page/${encodeURIComponent(title)}?token=${token}`);
      const pageData = response.data;
      
      // Handle redirect pages
      if (pageData.is_redirect && pageData.redirect_target) {
        // Navigate to the redirect target
        const redirectTarget = pageData.redirect_target.replace(/ /g, '_');
        navigate(`/wiki/${encodeURIComponent(redirectTarget)}`, { 
          replace: true,
          state: { redirectedFrom: title }
        });
        return;
      }
      
      setPage(pageData);
    } catch (error) {
      if (error.response?.status === 404) {
        setPage({ notFound: true });
      } else {
        toast.error("Failed to load page");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNotes = async () => {
    const token = localStorage.getItem("wiki_token");
    if (!token) return;

    try {
      const response = await axios.get(`${API}/wiki/notes/${encodeURIComponent(title)}?token=${token}`);
      setNotes(response.data);
    } catch (error) {
      setNotes({ content: "", page_title: title });
    }
  };

  const fetchHistory = async () => {
    const token = localStorage.getItem("wiki_token");
    if (!token) return;

    try {
      const response = await axios.get(`${API}/wiki/history/${encodeURIComponent(title)}?token=${token}`);
      setHistory(response.data.history || []);
      setCurrentVersion(response.data.current);
    } catch (error) {
      toast.error("Failed to load history");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("wiki_token");
    localStorage.removeItem("wiki_username");
    setIsAuthenticated(false);
    toast.success("Logged out successfully");
  };

  const handleDownloadWord = async () => {
    const token = localStorage.getItem("wiki_token");
    if (!token) {
      toast.error("Please log in to download");
      return;
    }

    try {
      toast.info("Generating Word document...");
      
      const response = await axios.get(
        `${API}/wiki/page/${encodeURIComponent(title)}/export?token=${token}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${title.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_')}.docx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Document downloaded successfully!");
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const handleDeletePage = async () => {
    const token = localStorage.getItem("wiki_token");
    if (!token) {
      toast.error("Please log in to delete");
      return;
    }

    setIsDeleting(true);
    try {
      await axios.delete(`${API}/wiki/page/${encodeURIComponent(title)}?token=${token}`);
      toast.success("Page deleted successfully");
      navigate("/wiki");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete page");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleMovePage = async () => {
    const token = localStorage.getItem("wiki_token");
    if (!token) {
      toast.error("Please log in to move page");
      return;
    }

    if (!newTitle.trim()) {
      toast.error("Please enter a new title");
      return;
    }

    if (newTitle.trim() === title) {
      toast.error("New title must be different from current title");
      return;
    }

    setIsMoving(true);
    try {
      await axios.post(`${API}/wiki/page/${encodeURIComponent(title)}/move?token=${token}`, {
        new_title: newTitle.trim(),
        reason: moveReason.trim(),
        create_redirect: createRedirect
      });
      toast.success(`Page moved to "${newTitle.trim()}"`);
      navigate(`/wiki/${encodeURIComponent(newTitle.trim().replace(/ /g, '_'))}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to move page");
    } finally {
      setIsMoving(false);
      setShowMoveModal(false);
      setNewTitle("");
      setMoveReason("");
    }
  };

  const openMoveModal = () => {
    setNewTitle(title);
    setMoveReason("");
    setCreateRedirect(true);
    setShowMoveModal(true);
  };

  const setActiveTab = (tab) => {
    if (tab === 'article') {
      setSearchParams({});
    } else {
      setSearchParams({ tab });
    }
    setViewingVersion(null);
    setShowDiff(false);
    setSelectedVersions([]);
  };

  const handleVersionSelect = (versionDate) => {
    if (selectedVersions.includes(versionDate)) {
      setSelectedVersions(prev => prev.filter(v => v !== versionDate));
    } else if (selectedVersions.length < 2) {
      setSelectedVersions(prev => [...prev, versionDate]);
    } else {
      setSelectedVersions([selectedVersions[1], versionDate]);
    }
  };

  const handleViewVersion = async (versionDate) => {
    const token = localStorage.getItem("wiki_token");
    if (!token) return;

    try {
      const response = await axios.get(
        `${API}/wiki/history/${encodeURIComponent(title)}/version/${versionDate}?token=${token}`
      );
      setViewingVersion(response.data);
      setShowDiff(false);
    } catch (error) {
      toast.error("Failed to load version");
    }
  };

  const handleCompare = async () => {
    if (selectedVersions.length !== 2) {
      toast.error("Select exactly 2 versions to compare");
      return;
    }
    setShowDiff(true);
    setViewingVersion(null);
  };

  const handleRestore = async (versionDate) => {
    if (!window.confirm("Are you sure you want to restore this version? The current version will be saved to history.")) {
      return;
    }

    const token = localStorage.getItem("wiki_token");
    if (!token) return;

    try {
      await axios.post(
        `${API}/wiki/history/${encodeURIComponent(title)}/restore/${versionDate}?token=${token}`
      );
      toast.success("Page restored successfully!");
      fetchPage(token);
      fetchHistory();
      setViewingVersion(null);
      setActiveTab('article');
    } catch (error) {
      toast.error("Failed to restore version");
    }
  };

  const getVersionContent = (versionDate) => {
    if (versionDate === 'current') {
      return currentVersion?.content || '';
    }
    const version = history.find(h => h.version_date === versionDate);
    return version?.content || '';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f6f6f6] flex items-center justify-center">
        <div className="text-[#72777d]">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#f6f6f6]">
        <WikiHeader username="" onLogout={handleLogout} showSearch={false} />
        <div className="max-w-[800px] mx-auto mt-12 px-4">
          <div className="bg-white border border-[#a2a9b1] p-6">
            <h1 className="font-wiki text-2xl text-[#202122] mb-4 border-b border-[#a2a9b1] pb-3">
              Access Restricted
            </h1>
            <p className="text-[#202122]">
              Please{" "}
              <Link to="/wiki/login" className="text-[#3366cc] hover:underline">
                log in
              </Link>{" "}
              to view this page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f6f6]">
      <WikiHeader username={username} onLogout={handleLogout} showSearch={true} />

      {/* Article/Notes/History Tabs */}
      <div className="bg-[#f6f6f6] border-b border-[#a2a9b1]">
        <div className="max-w-[1600px] mx-auto px-4">
          <div className="flex -mb-px">
            <button
              onClick={() => setActiveTab('article')}
              className={`py-2 px-4 text-sm border border-b-0 rounded-t transition-colors ${
                activeTab === 'article'
                  ? 'bg-white text-[#202122] border-[#a2a9b1] font-medium'
                  : 'bg-[#eaecf0] text-[#3366cc] border-transparent hover:bg-[#e1e3e5]'
              }`}
            >
              Article
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`py-2 px-4 text-sm border border-b-0 rounded-t ml-1 transition-colors ${
                activeTab === 'notes'
                  ? 'bg-white text-[#202122] border-[#a2a9b1] font-medium'
                  : 'bg-[#eaecf0] text-[#3366cc] border-transparent hover:bg-[#e1e3e5]'
              }`}
            >
              Notes
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-2 px-4 text-sm border border-b-0 rounded-t ml-1 transition-colors flex items-center gap-1 ${
                activeTab === 'history'
                  ? 'bg-white text-[#202122] border-[#a2a9b1] font-medium'
                  : 'bg-[#eaecf0] text-[#3366cc] border-transparent hover:bg-[#e1e3e5]'
              }`}
            >
              <History className="w-4 h-4" />
              View History
            </button>
          </div>
        </div>
      </div>

      {/* Action Tabs */}
      <div className="bg-white border-b border-[#a2a9b1]">
        <div className="max-w-[1600px] mx-auto px-4">
          <div className="flex gap-4 -mb-px items-center">
            <span className="py-2 px-4 text-sm border-b-2 border-[#3366cc] text-[#202122] font-medium">
              Read
            </span>
            <Link 
              to={activeTab === 'notes' 
                ? `/wiki/${encodeURIComponent(title)}/edit?tab=notes`
                : `/wiki/${encodeURIComponent(title)}/edit`
              }
              className="py-2 px-4 text-sm text-[#3366cc] hover:underline"
            >
              Edit
            </Link>
            {activeTab === 'article' && page && !page.notFound && (
              <button
                onClick={handleDownloadWord}
                className="py-2 px-4 text-sm text-[#3366cc] hover:underline flex items-center gap-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Download as Word
              </button>
            )}
            
            {/* Delete button - right aligned */}
            {page && !page.notFound && (
              <div className="ml-auto flex items-center gap-2">
                {/* Move button */}
                <button
                  onClick={openMoveModal}
                  className="py-2 px-4 text-sm text-[#3366cc] hover:text-[#2a4b8d] hover:underline flex items-center gap-1"
                  data-testid="wiki-move-btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8L22 12L18 16"/>
                    <path d="M2 12H22"/>
                    <path d="M6 8L2 12L6 16"/>
                  </svg>
                  Move
                </button>

                {/* Delete button */}
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="py-2 px-4 text-sm text-red-600 hover:text-red-800 hover:underline flex items-center gap-1"
                  data-testid="wiki-delete-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>

                {/* Move modal */}
                {showMoveModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4">
                      <h3 className="text-lg font-bold text-[#202122] mb-4">Move Page</h3>
                      <p className="text-sm text-[#54595d] mb-4">
                        Move "<strong>{title}</strong>" to a new title. This will change the page's URL.
                      </p>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-[#202122] mb-1">New title</label>
                          <input
                            type="text"
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            className="w-full px-3 py-2 border border-[#a2a9b1] rounded focus:outline-none focus:border-[#3366cc]"
                            placeholder="Enter new page title"
                            data-testid="wiki-move-new-title"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-[#202122] mb-1">Reason (optional)</label>
                          <input
                            type="text"
                            value={moveReason}
                            onChange={(e) => setMoveReason(e.target.value)}
                            className="w-full px-3 py-2 border border-[#a2a9b1] rounded focus:outline-none focus:border-[#3366cc]"
                            placeholder="Why is this page being moved?"
                          />
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="createRedirect"
                            checked={createRedirect}
                            onChange={(e) => setCreateRedirect(e.target.checked)}
                            className="rounded border-[#a2a9b1]"
                          />
                          <label htmlFor="createRedirect" className="text-sm text-[#54595d]">
                            Leave a redirect behind
                          </label>
                        </div>
                      </div>

                      <div className="flex gap-3 justify-end mt-6">
                        <button
                          onClick={() => setShowMoveModal(false)}
                          className="px-4 py-2 text-sm border border-[#a2a9b1] rounded hover:bg-[#eaecf0]"
                          disabled={isMoving}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleMovePage}
                          className="px-4 py-2 text-sm bg-[#3366cc] text-white rounded hover:bg-[#2a4b8d] flex items-center gap-2"
                          disabled={isMoving || !newTitle.trim() || newTitle.trim() === title}
                          data-testid="wiki-move-confirm-btn"
                        >
                          {isMoving ? (
                            <>
                              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                              </svg>
                              Moving...
                            </>
                          ) : (
                            "Move Page"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Delete confirmation modal */}
                {showDeleteConfirm && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                      <h3 className="text-lg font-bold text-[#202122] mb-4">Delete Page</h3>
                      <p className="text-sm text-[#54595d] mb-6">
                        Are you sure you want to delete "<strong>{title}</strong>"? This action cannot be undone.
                      </p>
                      <div className="flex gap-3 justify-end">
                        <button
                          onClick={() => setShowDeleteConfirm(false)}
                          className="px-4 py-2 text-sm border border-[#a2a9b1] rounded hover:bg-[#eaecf0]"
                          disabled={isDeleting}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDeletePage}
                          className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2"
                          disabled={isDeleting}
                          data-testid="wiki-delete-confirm-btn"
                        >
                          {isDeleting ? (
                            <>
                              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                              </svg>
                              Deleting...
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4" />
                              Delete Page
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1600px] mx-auto px-4 py-6">
        <div className="flex gap-6">
          <WikiSidebar username={username} />

          {/* Main Content */}
          <main className="flex-1 bg-white border border-[#a2a9b1] p-6 overflow-hidden min-w-0">
            {activeTab === 'article' ? (
              // Article Content
              page?.notFound ? (
                <>
                  <h1 className="font-wiki text-3xl text-[#202122] mb-4 border-b border-[#a2a9b1] pb-3">
                    {decodeURIComponent(title)}
                  </h1>
                  <p className="text-[#202122] mb-4">
                    There is currently no text in this page. You can{" "}
                    <Link 
                      to={`/wiki/${encodeURIComponent(title)}/edit`}
                      className="text-[#3366cc] hover:underline"
                    >
                      create this page
                    </Link>
                    .
                  </p>
                </>
              ) : (
                <>
                  <h1 className="font-wiki text-3xl text-[#202122] mb-4 border-b border-[#a2a9b1] pb-3">
                    {page?.title}
                  </h1>
                  
                  <div 
                    className="wiki-content prose max-w-none text-[#202122]"
                    dangerouslySetInnerHTML={{ __html: page?.html_content || "" }}
                  />
                  
                  {page?.updated_at && (
                    <div className="mt-8 pt-4 border-t border-[#a2a9b1] text-xs text-[#72777d]">
                      Last edited: {new Date(page.updated_at).toLocaleString()}
                    </div>
                  )}
                </>
              )
            ) : activeTab === 'notes' ? (
              // Notes Content - parsed as wikitext like main articles
              <>
                <h1 className="font-wiki text-3xl text-[#202122] mb-4 border-b border-[#a2a9b1] pb-3">
                  Notes: {decodeURIComponent(title)}
                </h1>
                
                {notes?.content ? (
                  <>
                    <div 
                      className="wiki-content prose max-w-none text-[#202122]"
                      dangerouslySetInnerHTML={{ __html: notes.parsed_html || notes.content }}
                    />
                    
                    {notes?.updated_at && (
                      <div className="mt-8 pt-4 border-t border-[#a2a9b1] text-xs text-[#72777d]">
                        Last edited: {new Date(notes.updated_at).toLocaleString()}
                        {notes.updated_by && ` by ${notes.updated_by}`}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-[#72777d] italic">
                    <p className="mb-4">There are no notes for this page yet.</p>
                    <p>
                      <Link 
                        to={`/wiki/${encodeURIComponent(title)}/edit?tab=notes`}
                        className="text-[#3366cc] hover:underline"
                      >
                        Add notes
                      </Link>
                      {" "}to this article for internal discussion, research notes, or draft content.
                    </p>
                  </div>
                )}
              </>
            ) : (
              // History Tab
              <>
                <h1 className="font-wiki text-3xl text-[#202122] mb-4 border-b border-[#a2a9b1] pb-3">
                  Revision history: {decodeURIComponent(title)}
                </h1>

                {viewingVersion ? (
                  // Viewing a specific version
                  <div>
                    <div className="bg-[#fff8e6] border border-[#ffcc00] p-3 mb-4 rounded flex items-center justify-between">
                      <span className="text-[#665500]">
                        <strong>Viewing old version</strong> from {new Date(viewingVersion.version_date).toLocaleString()}
                        {viewingVersion.edited_by && ` by ${viewingVersion.edited_by}`}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleRestore(viewingVersion.version_date)}
                          className="flex items-center gap-1 px-3 py-1 bg-[#3366cc] text-white rounded text-sm hover:bg-[#2a4b8d]"
                        >
                          <RotateCcw className="w-4 h-4" />
                          Restore this version
                        </button>
                        <button
                          onClick={() => setViewingVersion(null)}
                          className="px-3 py-1 border border-[#a2a9b1] rounded text-sm hover:bg-[#eaecf0]"
                        >
                          Back to history
                        </button>
                      </div>
                    </div>
                    <div className="border border-[#a2a9b1] p-4 rounded bg-[#f8f9fa]">
                      <pre className="whitespace-pre-wrap font-mono text-sm">{viewingVersion.content}</pre>
                    </div>
                  </div>
                ) : showDiff && selectedVersions.length === 2 ? (
                  // Showing diff between two versions
                  <div>
                    <div className="bg-[#eaf3ff] border border-[#a7d7f9] p-3 mb-4 rounded flex items-center justify-between">
                      <span className="text-[#202122]">
                        <strong>Comparing versions:</strong>{" "}
                        {selectedVersions[0] === 'current' ? 'Current' : new Date(selectedVersions[0]).toLocaleDateString()} ↔{" "}
                        {selectedVersions[1] === 'current' ? 'Current' : new Date(selectedVersions[1]).toLocaleDateString()}
                      </span>
                      <button
                        onClick={() => { setShowDiff(false); setSelectedVersions([]); }}
                        className="px-3 py-1 border border-[#a2a9b1] rounded text-sm hover:bg-[#eaecf0]"
                      >
                        Back to history
                      </button>
                    </div>
                    <DiffView 
                      oldText={getVersionContent(selectedVersions[0])}
                      newText={getVersionContent(selectedVersions[1])}
                    />
                  </div>
                ) : (
                  // History list
                  <div>
                    {selectedVersions.length > 0 && (
                      <div className="mb-4 p-3 bg-[#f8f9fa] border border-[#a2a9b1] rounded flex items-center justify-between">
                        <span className="text-sm">
                          Selected {selectedVersions.length} version(s) for comparison
                        </span>
                        <button
                          onClick={handleCompare}
                          disabled={selectedVersions.length !== 2}
                          className="px-3 py-1 bg-[#3366cc] text-white rounded text-sm hover:bg-[#2a4b8d] disabled:opacity-50"
                        >
                          Compare selected versions
                        </button>
                      </div>
                    )}

                    <table className="w-full text-sm">
                      <thead className="bg-[#eaecf0]">
                        <tr>
                          <th className="p-2 text-left w-8">
                            <span className="sr-only">Select</span>
                          </th>
                          <th className="p-2 text-left">Date/Time</th>
                          <th className="p-2 text-left">User</th>
                          <th className="p-2 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Current version */}
                        {currentVersion && (
                          <tr className="border-b border-[#eaecf0] bg-[#f8fff8]">
                            <td className="p-2">
                              <input
                                type="checkbox"
                                checked={selectedVersions.includes('current')}
                                onChange={() => handleVersionSelect('current')}
                              />
                            </td>
                            <td className="p-2">
                              <strong>{currentVersion.version_date ? new Date(currentVersion.version_date).toLocaleString() : 'Current'}</strong>
                              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Current</span>
                            </td>
                            <td className="p-2">{currentVersion.edited_by || 'Unknown'}</td>
                            <td className="p-2">—</td>
                          </tr>
                        )}
                        {/* Historical versions */}
                        {history.map((version, idx) => (
                          <tr key={idx} className="border-b border-[#eaecf0] hover:bg-[#f8f9fa]">
                            <td className="p-2">
                              <input
                                type="checkbox"
                                checked={selectedVersions.includes(version.version_date)}
                                onChange={() => handleVersionSelect(version.version_date)}
                              />
                            </td>
                            <td className="p-2">
                              {new Date(version.version_date).toLocaleString()}
                            </td>
                            <td className="p-2">{version.edited_by || 'Unknown'}</td>
                            <td className="p-2">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleViewVersion(version.version_date)}
                                  className="flex items-center gap-1 text-[#3366cc] hover:underline"
                                >
                                  <Eye className="w-4 h-4" />
                                  View
                                </button>
                                <button
                                  onClick={() => handleRestore(version.version_date)}
                                  className="flex items-center gap-1 text-[#3366cc] hover:underline"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                  Restore
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {history.length === 0 && !currentVersion && (
                          <tr>
                            <td colSpan={4} className="p-4 text-center text-[#72777d]">
                              No revision history available yet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      <style>{`
        .wiki-content {
          overflow: hidden;
          overflow-wrap: break-word;
          word-wrap: break-word;
          word-break: break-word;
          max-width: 100%;
        }
        /* Ensure all content respects container bounds */
        .wiki-content * {
          max-width: 100%;
        }
        .wiki-content img {
          height: auto;
        }
        .wiki-content pre,
        .wiki-content code {
          overflow-x: auto;
          white-space: pre-wrap;
          word-wrap: break-word;
        }
        /* Inline icon templates - ensure SVG icons display inline with text */
        .wiki-content svg {
          display: inline !important;
          vertical-align: middle;
        }
        .wiki-content h2 {
          font-size: 1.5rem;
          font-weight: normal;
          border-bottom: 1px solid #a2a9b1;
          padding-bottom: 0.5rem;
          margin-top: 2rem;
          margin-bottom: 1rem;
          /* Note: No clear property - allows text to wrap around floated infobox */
        }
        .wiki-content h3 {
          font-size: 1.17rem;
          font-weight: bold;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .wiki-content h4 {
          font-size: 1rem;
          font-weight: bold;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
        }
        /* Section edit links - Wikipedia style */
        .wiki-content .mw-editsection {
          font-size: 0.75rem;
          font-weight: normal;
          font-family: sans-serif;
          margin-left: 0.5em;
          vertical-align: baseline;
          line-height: 1;
        }
        .wiki-content .mw-editsection-bracket {
          color: #54595d;
        }
        .wiki-content .mw-editsection-link {
          color: #3366cc;
          text-decoration: none;
        }
        .wiki-content .mw-editsection-link:hover {
          text-decoration: underline;
        }
        .wiki-content .mw-editsection-divider {
          color: #54595d;
        }
        .wiki-content p {
          margin-top: 0;
          margin-bottom: 0.5em;
          line-height: 1.6;
        }
        .wiki-content p + p {
          margin-top: 4px;
        }
        .wiki-content a {
          color: #3366cc;
        }
        .wiki-content a:hover {
          text-decoration: underline;
        }
        .wiki-content ul {
          margin-left: 1.5rem;
          margin-bottom: 1rem;
          list-style-type: disc;
        }
        .wiki-content ol {
          margin-left: 1.5rem;
          margin-bottom: 1rem;
          list-style-type: decimal;
        }
        .wiki-content li {
          margin-bottom: 0.25rem;
          line-height: 1.6;
        }
        .wiki-content sup.reference {
          font-size: 0.75rem;
          line-height: 1;
          vertical-align: super;
        }
        .wiki-content sup.reference a {
          color: #3366cc;
          text-decoration: none;
        }
        .wiki-content .infobox {
          float: right;
          clear: right;
          margin: 0.5rem 0 1rem 1rem;
          width: 22rem;
          max-width: 40%;
          font-size: 0.875rem;
          border: 1px solid #a2a9b1;
          background-color: #f8f9fa;
          border-collapse: collapse;
          box-sizing: border-box;
        }
        @media (max-width: 768px) {
          .wiki-content .infobox {
            float: none;
            width: 100%;
            max-width: 100%;
            margin: 1rem 0;
          }
        }
        .wiki-content .infobox img {
          display: block;
          margin: 0 auto;
        }
        .wiki-content .infobox-title {
          text-align: center;
          font-size: 1.1rem;
          font-weight: bold;
          padding: 0.5rem;
          background-color: #eaecf0;
          border: 1px solid #a2a9b1;
        }
        .wiki-content .infobox th {
          text-align: left;
          padding: 0.35rem 0.5rem;
          vertical-align: top;
          font-weight: normal;
          background-color: #eaecf0;
          border: 1px solid #a2a9b1;
          width: 40%;
        }
        .wiki-content .infobox td {
          padding: 0.35rem 0.5rem;
          vertical-align: top;
          border: 1px solid #a2a9b1;
        }
        
        /* Wikitable styles */
        .wiki-content .wikitable {
          border-collapse: collapse;
          margin: 1rem 0;
          background-color: #f8f9fa;
        }
        .wiki-content .wikitable th,
        .wiki-content .wikitable td {
          border: 1px solid #a2a9b1;
          padding: 0.35rem 0.65rem;
          vertical-align: top;
        }
        .wiki-content .wikitable th {
          background-color: #eaecf0;
          text-align: left;
          font-weight: bold;
        }
        .wiki-content .wikitable tr:nth-child(even) td {
          background-color: #f8f9fa;
        }
        .wiki-content .wikitable tr:nth-child(odd) td {
          background-color: #ffffff;
        }
        
        /* Sortable table styles */
        .wiki-content .wikitable.sortable th.sortable-header {
          cursor: pointer;
          position: relative;
          padding-right: 1.5rem;
          user-select: none;
        }
        .wiki-content .wikitable.sortable th.sortable-header:hover {
          background-color: #dde1e6;
        }
        .wiki-content .wikitable.sortable th.sortable-header::after {
          content: "⇅";
          position: absolute;
          right: 0.4rem;
          top: 50%;
          transform: translateY(-50%);
          font-size: 0.8rem;
          color: #72777d;
          opacity: 0.6;
        }
        .wiki-content .wikitable.sortable th.sortable-header.sort-asc::after {
          content: "▲";
          opacity: 1;
          color: #36c;
        }
        .wiki-content .wikitable.sortable th.sortable-header.sort-desc::after {
          content: "▼";
          opacity: 1;
          color: #36c;
        }
        .wiki-content .wikitable.sortable th.unsortable {
          cursor: default;
        }
        .wiki-content .wikitable.sortable th.unsortable::after {
          content: none;
        }
        
        .wiki-content .reflist {
          margin-top: 2rem;
          font-size: 0.9rem;
        }
        .wiki-content .references {
          margin-left: 2rem;
          line-height: 1.5;
        }
        .wiki-content .references li {
          margin-bottom: 0.5rem;
          word-wrap: break-word;
          transition: background-color 0.3s ease-out;
        }
        .wiki-content .references li.citation-highlight {
          background-color: #ffe58f;
          animation: citation-fade 3s ease-out forwards;
        }
        @keyframes citation-fade {
          0% { background-color: #ffe58f; }
          70% { background-color: #ffe58f; }
          100% { background-color: transparent; }
        }
        /* Reference backlinks - Wikipedia style */
        .wiki-content .ref-backlink-wrap {
          margin-right: 0.3em;
        }
        .wiki-content .ref-backlink {
          color: #3366cc;
          text-decoration: none;
        }
        .wiki-content .ref-backlink:hover {
          text-decoration: underline;
        }
        .wiki-content .ref-backlinks {
          white-space: nowrap;
        }
        .wiki-content .ref-backlink-letter {
          color: #3366cc;
          text-decoration: none;
          margin: 0 0.15em;
        }
        .wiki-content .ref-backlink-letter:hover {
          text-decoration: underline;
        }
        .wiki-content .ref-backlink-letter sup {
          font-size: 0.85em;
        }
        .wiki-content .hatnote {
          font-style: italic;
          padding-left: 1.5rem;
          margin-bottom: 0.5rem;
          color: #555;
        }
        .wiki-content b {
          font-weight: bold;
        }
        .wiki-content i {
          font-style: italic;
        }
        .wiki-content s, .wiki-content strike, .wiki-content del {
          text-decoration: line-through;
        }
        .wiki-content u, .wiki-content ins {
          text-decoration: underline;
        }
        .wiki-content .reflist {
          clear: both;
        }
        
        /* List styles - Wikipedia style */
        .wiki-content .unbulleted-list,
        .wiki-content .plainlist {
          list-style: none;
          margin-left: 0;
          padding-left: 1.5rem;
        }
        .wiki-content .unbulleted-list li,
        .wiki-content .plainlist li {
          margin-bottom: 0.25rem;
        }
        .wiki-content .hlist,
        .wiki-content .flatlist {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-wrap: wrap;
          gap: 0;
        }
        .wiki-content .hlist li,
        .wiki-content .flatlist li {
          display: inline;
        }
        .wiki-content .hlist li:not(:last-child)::after,
        .wiki-content .flatlist li:not(:last-child)::after {
          content: " · ";
          color: #54595d;
        }
        
        /* Collapse box styles - Wikipedia style */
        .wiki-collapse-box {
          border: 1px solid #a2a9b1;
          margin: 1rem 0;
          background-color: #f8f9fa;
          border-radius: 2px;
          overflow: hidden;
        }
        .wiki-collapse-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: linear-gradient(to bottom, #f8f9fa, #eaecf0);
          border-bottom: 1px solid #a2a9b1;
          cursor: pointer;
          user-select: none;
        }
        .wiki-collapse-header:hover {
          background: linear-gradient(to bottom, #eaecf0, #dde0e4);
        }
        .wiki-collapse-toggle {
          font-size: 0.75em;
          color: #3366cc;
          transition: transform 0.2s;
          width: 12px;
        }
        .wiki-collapse-title {
          font-weight: 600;
          color: #202122;
          flex: 1;
        }
        .wiki-collapse-hint {
          font-size: 0.8em;
          color: #3366cc;
          font-weight: normal;
        }
        .wiki-collapse-content {
          padding: 12px;
          background-color: #fff;
        }
        
        /* Nowiki content - displays as literal code */
        .wiki-content code.nowiki-content {
          font-family: monospace;
          background-color: #f8f9fa;
          padding: 0 4px;
          border-radius: 2px;
          font-size: 0.9em;
          color: #202122;
        }
        
        /* TextDiff - ensure ins/del colors override code background */
        .wiki-content .text-diff ins {
          background-color: #caffca !important;
          color: #006400 !important;
          text-decoration: none;
          display: inline;
        }
        .wiki-content .text-diff del {
          background-color: #ffcaca !important;
          color: #8b0000 !important;
          text-decoration: line-through;
          display: inline;
        }
        .wiki-content .text-diff ins code.nowiki-content,
        .wiki-content .text-diff del code.nowiki-content,
        .wiki-content .text-diff ins code.nowiki-content *,
        .wiki-content .text-diff del code.nowiki-content * {
          background-color: inherit !important;
          color: inherit !important;
        }
        /* Ensure p tags inside ins/del don't break layout */
        .wiki-content .text-diff ins p,
        .wiki-content .text-diff del p {
          display: inline;
          margin: 0;
          padding: 0;
        }
        
        /* Indentation using : wikicode (dl/dd elements) */
        .wiki-content dl {
          margin: 0;
          padding: 0;
        }
        .wiki-content dd {
          margin-left: 1.6em;
          margin-top: 0.25em;
          margin-bottom: 0.25em;
        }
        .wiki-content dl dl dd {
          margin-left: 1.6em;
        }
      `}</style>
    </div>
  );
}
